import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import * as THREE from 'three';
import { COLORMAPS, hexRgba } from './colormaps';
import { formatFtInSymbol } from './units';
import ColormapPicker from './ColormapPicker';

// Rotation / zoom constraints (shared with RoomViz)
const AZ_MIN = -(Math.PI - 0.05);
const AZ_MAX = -(Math.PI / 2 + 0.05);
const EL_MIN = 0.05;
const EL_MAX = Math.PI / 2 - 0.1;

// ── Face pressure shader ───────────────────────────────────────────────────────
const FACE_VERT = `
attribute vec3 a_phys;
varying vec3 v_phys;
void main() {
  v_phys = a_phys;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FACE_FRAG = `
precision mediump float;
uniform sampler2D u_lut;
uniform float u_mode0Active;
uniform float u_mode0Nx, u_mode0Ny, u_mode0Nz;
uniform float u_mode1Active;
uniform float u_mode1Nx, u_mode1Ny, u_mode1Nz;
uniform float u_blend;
uniform float u_phase;
uniform vec3 u_fillColor;
uniform float u_fillOpacity;
varying vec3 v_phys;

const float PI = 3.14159265358979;

vec4 modeColor(float nx, float ny, float nz) {
  float p = cos(nx * PI * v_phys.x) * cos(ny * PI * v_phys.y) * cos(nz * PI * v_phys.z) * cos(u_phase);
  return texture2D(u_lut, vec2(clamp((p + 1.0) * 0.5, 0.001, 0.999), 0.5));
}

void main() {
  vec4 fill = vec4(u_fillColor, u_fillOpacity);
  vec4 c1 = u_mode1Active > 0.5 ? modeColor(u_mode1Nx, u_mode1Ny, u_mode1Nz) : fill;
  vec4 c0 = u_mode0Active > 0.5 ? modeColor(u_mode0Nx, u_mode0Ny, u_mode0Nz) : fill;
  gl_FragColor = mix(c0, c1, u_blend);
}
`;

// ── Isosurface ray-march shader ────────────────────────────────────────────────
// Ports isoWorker.js exactly: same ray-origin math, slab test, march loop,
// 4 iso levels, alpha compositing, and LUT color lookup.
const ISO_VERT = `
void main() {
  // Bypass camera — output clip-space position directly.
  // PlaneGeometry(2,2) vertices span (-1,-1) to (1,1) = full viewport.
  gl_Position = vec4(position.xy, 0.999, 1.0);
}
`;

const ISO_FRAG = `
precision mediump float;

uniform sampler2D u_lut;
uniform float u_mode1Active;
uniform float u_mode1Nx, u_mode1Ny, u_mode1Nz;
uniform float u_blend;       // 0-1: fade-in opacity on mode change
uniform float u_phase;
uniform float u_isoVisible;  // 0 or 1 (ISO toggle)

// Room dimensions in 3D-scene units (max dim = 100)
uniform float u_W3, u_H3, u_D3;

// Camera angles — same values used in the render effect and isoWorker
uniform float u_cosAz, u_sinAz, u_cosEl, u_sinEl;

// 2D projection parameters — identical to isoWorker job fields
uniform float u_minU, u_minV, u_sf;
uniform float u_offX, u_offY, u_containerH;
uniform float u_dpr;   // device pixel ratio (gl_FragCoord is in device pixels)
uniform float u_step;  // ray march step size in scene units

const float PI = 3.14159265358979;
const float ISO_ALPHA = 0.5;
const float EPS = 1e-6;

float pressure(float px, float py, float pz) {
  // px/W3 = x/Lx (width), pz/D3 = y/Ly (depth), py/H3 = z/Lz (height)
  // Matches isoWorker.js: nx*PI*px/W3, ny*PI*pz/D3, nz*PI*py/H3
  return cos(u_mode1Nx * PI * px / u_W3) *
         cos(u_mode1Ny * PI * pz / u_D3) *
         cos(u_mode1Nz * PI * py / u_H3) *
         cos(u_phase);
}

void accumulate(float prevP, float curP, float level, inout float accR, inout float accG, inout float accB, inout float accA) {
  if ((prevP - level) * (curP - level) < 0.0) {
    float t = clamp((level + 1.0) * 0.5, 0.001, 0.999);
    vec3 c = texture2D(u_lut, vec2(t, 0.5)).rgb;
    float w = ISO_ALPHA * (1.0 - accA);
    accR += c.r * w;
    accG += c.g * w;
    accB += c.b * w;
    accA += w;
  }
}

void main() {
  if (u_mode1Active < 0.5 || u_isoVisible < 0.5 || u_blend < 0.001) {
    gl_FragColor = vec4(0.0);
    return;
  }

  // Reconstruct projected (u,v) from gl_FragCoord, matching isoWorker.js.
  // gl_FragCoord is in device pixels (WebGL origin = bottom-left).
  // Worker uses top-left origin, so flip y: cy = containerH*dpr - gl_FragCoord.y
  float u = (gl_FragCoord.x / u_dpr - u_offX) / u_sf + u_minU;
  float v = ((u_containerH * u_dpr - gl_FragCoord.y) / u_dpr - u_offY) / u_sf + u_minV;

  // Ray origin on the plane through room center, perpendicular to view direction
  // (same as isoWorker lines 28-30, shifted by +W3/2, +H3/2, +D3/2)
  float px0 = u * u_cosAz - v * u_sinEl * u_sinAz + u_W3 * 0.5;
  float py0 = -v * u_cosEl                          + u_H3 * 0.5;
  float pz0 = u * u_sinAz + v * u_sinEl * u_cosAz  + u_D3 * 0.5;

  // Ray direction toward camera (same as isoWorker rDpx/y/z)
  float rDpx = -u_cosEl * u_sinAz;
  float rDpy =  u_sinEl;
  float rDpz =  u_cosEl * u_cosAz;

  // Slab test: clip ray to room bounding box [0,W3]×[0,H3]×[0,D3]
  float tMin = -1e5;
  float tMax =  1e5;

  if (abs(rDpx) > EPS) {
    float t1 = -px0 / rDpx;
    float t2 = (u_W3 - px0) / rDpx;
    tMin = max(tMin, min(t1, t2));
    tMax = min(tMax, max(t1, t2));
  } else if (px0 < 0.0 || px0 > u_W3) { gl_FragColor = vec4(0.0); return; }

  if (abs(rDpy) > EPS) {
    float t1 = -py0 / rDpy;
    float t2 = (u_H3 - py0) / rDpy;
    tMin = max(tMin, min(t1, t2));
    tMax = min(tMax, max(t1, t2));
  } else if (py0 < 0.0 || py0 > u_H3) { gl_FragColor = vec4(0.0); return; }

  if (abs(rDpz) > EPS) {
    float t1 = -pz0 / rDpz;
    float t2 = (u_D3 - pz0) / rDpz;
    tMin = max(tMin, min(t1, t2));
    tMax = min(tMax, max(t1, t2));
  } else if (pz0 < 0.0 || pz0 > u_D3) { gl_FragColor = vec4(0.0); return; }

  if (tMin >= tMax) { gl_FragColor = vec4(0.0); return; }

  // Ray march — 200 iterations covers a diagonal of 200*u_step units.
  // With u_step=2.0 and max room diagonal ~173, this is sufficient for any room.
  float accR = 0.0, accG = 0.0, accB = 0.0, accA = 0.0;
  float tc0 = tMin;
  float prevP = pressure(px0 + rDpx * tc0, py0 + rDpy * tc0, pz0 + rDpz * tc0);

  for (int i = 1; i < 200; i++) {
    if (accA > 0.97) break;
    float t = tMin + float(i) * u_step;
    if (t > tMax + u_step) break;
    float tc = min(t, tMax);
    float px = px0 + rDpx * tc;
    float py = py0 + rDpy * tc;
    float pz = pz0 + rDpz * tc;
    float curP = pressure(px, py, pz);

    accumulate(prevP, curP, -0.75, accR, accG, accB, accA);
    accumulate(prevP, curP, -0.35, accR, accG, accB, accA);
    accumulate(prevP, curP,  0.35, accR, accG, accB, accA);
    accumulate(prevP, curP,  0.75, accR, accG, accB, accA);

    prevP = curP;
    if (t >= tMax) break;
  }

  if (accA < 0.01) { gl_FragColor = vec4(0.0); return; }

  float safeA = max(accA, 0.001);
  float finalA = min(accA, 1.0) * u_blend;
  gl_FragColor = vec4(accR / safeA, accG / safeA, accB / safeA, finalA);
}
`;

// ── Normalized physical room coordinates per face vertex ──────────────────────
// Order: [c0,c1,c2,c0,c2,c3] for each face (two triangles).
// phys.x = xp/Lx (width), phys.y = yp/Ly (depth), phys.z = zp/Lz (height)
const FACE_PHYS = [
  new Float32Array([0,1,1, 1,1,1, 1,1,0, 0,1,1, 1,1,0, 0,1,0]), // back wall  [6,7,5,4]
  new Float32Array([0,0,1, 0,1,1, 0,1,0, 0,0,1, 0,1,0, 0,0,0]), // left wall  [2,6,4,0]
  new Float32Array([0,0,0, 1,0,0, 1,1,0, 0,0,0, 1,1,0, 0,1,0]), // floor      [0,1,5,4]
];

function easeInOut(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }

export default function RoomVizGL({ Lx, Ly, Lz, mode, imperial, theme, dimInteracting, activeColormap, setActiveColormap, lut, animPlaying, onPhaseUpdate, phaseResetTrigger }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);

  // Scene objects (created once in init effect)
  const faceMeshesRef = useRef(null);  // [backWall, leftWall, floor]
  const faceEdgesRef = useRef(null);   // [backEdges, leftEdges, floorEdges]
  const dashedRef = useRef(null);      // dashed depth-perception edges
  const isoMeshRef = useRef(null);     // full-screen iso ray-march quad

  // Label DOM refs (positioned imperatively in render effect)
  const labelLRef = useRef(null);
  const labelHRef = useRef(null);
  const labelWRef = useRef(null);

  // Phase 2/3 refs
  const lutTexRef = useRef(null);      // THREE.DataTexture for LUT
  const prevModeRef = useRef(null);    // previous mode for crossfade detection
  const cfRafRef = useRef(null);       // crossfade rAF id
  const animPhaseRef = useRef(0);      // current animation phase (radians)
  const animRafRef = useRef(null);     // animation rAF id

  // View state
  const [containerW, setContainerW] = useState(0);
  const [containerH, setContainerH] = useState(0);
  const [az, setAz] = useState(-Math.PI * 3 / 4);
  const [el, setEl] = useState(Math.PI / 6);
  const [isDragging, setIsDragging] = useState(false);
  const [showIso, setShowIso] = useState(true);
  const [zoom, setZoom] = useState(1.0);

  // Interaction refs
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const targetPoseRef = useRef({ az: -Math.PI * 3 / 4, el: Math.PI / 6, zoom: 1.0 });
  const poseRafRef = useRef(null);

  // ── ResizeObserver ──────────────────────────────────────────────
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) {
        setContainerW(e.contentRect.width);
        setContainerH(e.contentRect.height);
      }
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // ── Three.js init ──────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.sortObjects = false;
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -5000, 5000);
    cameraRef.current = camera;

    // Canvas setup
    const canvas = renderer.domElement;
    canvas.style.display = 'block';
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.webkitUserSelect = 'none';
    canvas.setAttribute('aria-label', 'Room geometry 3D view \u2014 drag to rotate, scroll or pinch to zoom');
    container.insertBefore(canvas, container.firstChild);

    // LUT DataTexture (256 × 1, RGBA) — data populated by lut update effect
    const lutData = new Uint8Array(256 * 4);
    const lutTex = new THREE.DataTexture(lutData, 256, 1, THREE.RGBAFormat);
    lutTex.needsUpdate = true;
    lutTexRef.current = lutTex;

    // 3 face meshes — ShaderMaterial with a_phys attribute
    const faceMeshes = Array.from({ length: 3 }, (_, fi) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(18), 3));
      const physAttr = new THREE.Float32BufferAttribute(FACE_PHYS[fi].slice(), 3);
      physAttr.needsUpdate = false;
      geo.setAttribute('a_phys', physAttr);
      geo.setIndex([0, 1, 2, 3, 4, 5]);

      const mat = new THREE.ShaderMaterial({
        vertexShader: FACE_VERT,
        fragmentShader: FACE_FRAG,
        transparent: true,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          u_lut:         { value: lutTex },
          u_mode0Active: { value: 0.0 },
          u_mode0Nx:     { value: 0.0 },
          u_mode0Ny:     { value: 0.0 },
          u_mode0Nz:     { value: 0.0 },
          u_mode1Active: { value: 0.0 },
          u_mode1Nx:     { value: 0.0 },
          u_mode1Ny:     { value: 0.0 },
          u_mode1Nz:     { value: 0.0 },
          u_blend:       { value: 1.0 },
          u_phase:       { value: 0.0 },
          u_fillColor:   { value: new THREE.Vector3(1, 1, 1) },
          u_fillOpacity: { value: 0.06 },
        },
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.frustumCulled = false;
      scene.add(mesh);
      return mesh;
    });
    faceMeshesRef.current = faceMeshes;

    // 3 face edge line-sets (4 segments = 8 verts each)
    const faceEdges = Array.from({ length: 3 }, () => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(24), 3));
      const mat = new THREE.LineBasicMaterial({
        transparent: true, depthTest: false, depthWrite: false,
      });
      const line = new THREE.LineSegments(geo, mat);
      line.frustumCulled = false;
      scene.add(line);
      return line;
    });
    faceEdgesRef.current = faceEdges;

    // Dashed depth edges (3 segments = 6 verts)
    const dashedGeo = new THREE.BufferGeometry();
    dashedGeo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(18), 3));
    const dashedMat = new THREE.LineDashedMaterial({
      transparent: true, depthTest: false, depthWrite: false,
      dashSize: 1, gapSize: 1,
    });
    const dashedLine = new THREE.LineSegments(dashedGeo, dashedMat);
    dashedLine.frustumCulled = false;
    dashedLine.renderOrder = 10;
    scene.add(dashedLine);
    dashedRef.current = dashedLine;

    // ISO ray-march quad — full-screen PlaneGeometry(2,2) in clip space
    const isoGeo = new THREE.PlaneGeometry(2, 2);
    const isoMat = new THREE.ShaderMaterial({
      vertexShader: ISO_VERT,
      fragmentShader: ISO_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        u_lut:        { value: lutTex },
        u_mode1Active: { value: 0.0 },
        u_mode1Nx:    { value: 0.0 },
        u_mode1Ny:    { value: 0.0 },
        u_mode1Nz:    { value: 0.0 },
        u_blend:      { value: 1.0 },
        u_phase:      { value: 0.0 },
        u_isoVisible: { value: 1.0 },
        u_W3:         { value: 100.0 },
        u_H3:         { value: 100.0 },
        u_D3:         { value: 100.0 },
        u_cosAz:      { value: 0.0 },
        u_sinAz:      { value: -1.0 },
        u_cosEl:      { value: 0.866 },
        u_sinEl:      { value: 0.5 },
        u_minU:       { value: -100.0 },
        u_minV:       { value: -100.0 },
        u_sf:         { value: 1.0 },
        u_offX:       { value: 0.0 },
        u_offY:       { value: 0.0 },
        u_containerH: { value: 0.0 },
        u_dpr:        { value: 1.0 },
        u_step:       { value: 2.0 },
      },
    });
    const isoMesh = new THREE.Mesh(isoGeo, isoMat);
    isoMesh.frustumCulled = false;
    isoMesh.renderOrder = 8;  // after face fills/edges (0-5), before dashed (10)
    scene.add(isoMesh);
    isoMeshRef.current = isoMesh;

    // Render order: back fill(0), back edges(1), left fill(2), left edges(3),
    //               floor fill(4), floor edges(5), iso(8), dashed(10)
    faceMeshes.forEach((m, i) => { m.renderOrder = i * 2; });
    faceEdges.forEach((l, i) => { l.renderOrder = i * 2 + 1; });

    return () => {
      if (cfRafRef.current) { cancelAnimationFrame(cfRafRef.current); cfRafRef.current = null; }
      if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; }
      faceMeshes.forEach(m => { m.geometry.dispose(); m.material.dispose(); });
      faceEdges.forEach(l => { l.geometry.dispose(); l.material.dispose(); });
      dashedLine.geometry.dispose();
      dashedLine.material.dispose();
      isoGeo.dispose();
      isoMat.dispose();
      lutTex.dispose();
      renderer.dispose();
      canvas.remove();
    };
  }, []);

  // ── LUT DataTexture update — runs whenever colormap changes ────
  useEffect(() => {
    const tex = lutTexRef.current;
    if (!tex) return;
    const data = tex.image.data;
    for (let i = 0; i < 256; i++) {
      data[i * 4]     = lut[i * 3];
      data[i * 4 + 1] = lut[i * 3 + 1];
      data[i * 4 + 2] = lut[i * 3 + 2];
      data[i * 4 + 3] = 255;
    }
    tex.needsUpdate = true;
    if (!animPlaying && rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  }, [lut, animPlaying]);

  // ── Render ─────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    const meshes = faceMeshesRef.current;
    const edges = faceEdgesRef.current;
    const dashed = dashedRef.current;
    const isoMesh = isoMeshRef.current;
    if (!renderer || !camera || !meshes || !edges || !dashed || !isoMesh) return;
    if (containerW === 0 || containerH === 0) return;

    renderer.setSize(containerW, containerH);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    // Room dimensions scaled to ~100-unit space (matches RoomViz)
    const sc = 100 / Math.max(Lx, Lz, Ly);
    const W3 = Lx * sc, H3 = Lz * sc, D3 = Ly * sc;
    const halfW = W3 / 2, halfH = H3 / 2, halfD = D3 / 2;

    // Projection math — identical to RoomViz, for frustum sizing + labels
    const cosAz = Math.cos(az), sinAz = Math.sin(az);
    const cosEl = Math.cos(el), sinEl = Math.sin(el);

    function proj(px, py, pz) {
      const x = px - halfW, y = py - halfH, z = pz - halfD;
      return { u: x * cosAz + z * sinAz, v: -(y * cosEl - (-x * sinAz + z * cosAz) * sinEl) };
    }

    const P = [
      proj(0, 0, 0),   proj(W3, 0, 0),  proj(0, H3, 0), proj(W3, H3, 0),
      proj(0, 0, D3),  proj(W3, 0, D3), proj(0, H3, D3), proj(W3, H3, D3),
    ];

    const us = P.map(p => p.u), vs = P.map(p => p.v);
    const pad = 32;
    const minU = Math.min(...us) - pad, maxU = Math.max(...us) + pad;
    const minV = Math.min(...vs) - pad, maxV = Math.max(...vs) + pad;
    const svgW = maxU - minU, svgH = maxV - minV;
    const sf = Math.min(containerW / svgW, containerH / svgH) * zoom;
    const offX = (containerW - svgW * sf) / 2;
    const offY = (containerH - svgH * sf) / 2;

    function toC(p) {
      return { x: (p.u - minU) * sf + offX, y: (p.v - minV) * sf + offY };
    }

    // ── Camera frustum & orientation ──
    const visW = containerW / sf, visH = containerH / sf;
    camera.left = -visW / 2;
    camera.right = visW / 2;
    camera.top = visH / 2;
    camera.bottom = -visH / 2;

    const camDist = 1000;
    camera.position.set(
      -cosEl * sinAz * camDist,
       sinEl * camDist,
       cosEl * cosAz * camDist,
    );
    camera.up.set(sinEl * sinAz, cosEl, -sinEl * cosAz);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    // ── Room corners (centered at origin) ──
    const C = [
      [-halfW, -halfH, -halfD], [ halfW, -halfH, -halfD],
      [-halfW,  halfH, -halfD], [ halfW,  halfH, -halfD],
      [-halfW, -halfH,  halfD], [ halfW, -halfH,  halfD],
      [-halfW,  halfH,  halfD], [ halfW,  halfH,  halfD],
    ];

    // ── Mode change detection — setup crossfade uniforms ──
    const modeKey = mode ? `${mode.nx},${mode.ny},${mode.nz}` : null;
    const prevKey = prevModeRef.current ? `${prevModeRef.current.nx},${prevModeRef.current.ny},${prevModeRef.current.nz}` : null;
    if (modeKey !== prevKey) {
      const mats = meshes.map(m => m.material);

      // If a crossfade is already running, snap it to completion first
      if (cfRafRef.current) {
        cancelAnimationFrame(cfRafRef.current);
        cfRafRef.current = null;
        mats.forEach(mat => { mat.uniforms.u_blend.value = 1.0; });
        isoMesh.material.uniforms.u_blend.value = 1.0;
      }

      // Promote current mode1 → mode0 for face meshes
      mats.forEach(mat => {
        mat.uniforms.u_mode0Active.value = mat.uniforms.u_mode1Active.value;
        mat.uniforms.u_mode0Nx.value = mat.uniforms.u_mode1Nx.value;
        mat.uniforms.u_mode0Ny.value = mat.uniforms.u_mode1Ny.value;
        mat.uniforms.u_mode0Nz.value = mat.uniforms.u_mode1Nz.value;
        mat.uniforms.u_blend.value = 0.0;
        mat.uniforms.u_mode1Active.value = mode ? 1.0 : 0.0;
        if (mode) {
          mat.uniforms.u_mode1Nx.value = mode.nx;
          mat.uniforms.u_mode1Ny.value = mode.ny;
          mat.uniforms.u_mode1Nz.value = mode.nz;
        }
      });

      // ISO: instantly switch to new mode, fade in from 0
      const iu = isoMesh.material.uniforms;
      iu.u_blend.value = 0.0;
      iu.u_mode1Active.value = mode ? 1.0 : 0.0;
      if (mode) {
        iu.u_mode1Nx.value = mode.nx;
        iu.u_mode1Ny.value = mode.ny;
        iu.u_mode1Nz.value = mode.nz;
      }

      prevModeRef.current = mode;

      // Start 380ms ease-in-out crossfade
      const start = performance.now();
      const duration = 380;
      const cfFaceMeshes = faceMeshesRef;
      const cfIsoMesh = isoMeshRef;
      const cfRenderer = rendererRef;
      const cfScene = sceneRef;
      const cfCamera = cameraRef;
      function cfStep(now) {
        const rawT = Math.min((now - start) / duration, 1.0);
        const t = easeInOut(rawT);
        cfFaceMeshes.current?.forEach(m => { m.material.uniforms.u_blend.value = t; });
        if (cfIsoMesh.current) cfIsoMesh.current.material.uniforms.u_blend.value = t;
        if (cfRenderer.current && cfScene.current && cfCamera.current) {
          cfRenderer.current.render(cfScene.current, cfCamera.current);
        }
        if (rawT < 1.0) {
          cfRafRef.current = requestAnimationFrame(cfStep);
        } else {
          cfRafRef.current = null;
        }
      }
      cfRafRef.current = requestAnimationFrame(cfStep);
    }

    // ── Update face fill colors + geometry positions ──
    const [paL, paH, paW] = COLORMAPS[activeColormap]._pa;
    const faceDefs = [
      { idx: [6, 7, 5, 4], pa: paW }, // back wall
      { idx: [2, 6, 4, 0], pa: paH }, // left wall
      { idx: [0, 1, 5, 4], pa: paL }, // floor
    ];

    faceDefs.forEach(({ idx, pa }, fi) => {
      const [c0, c1, c2, c3] = idx.map(i => C[i]);

      const pos = meshes[fi].geometry.attributes.position.array;
      [c0, c1, c2, c0, c2, c3].forEach((c, vi) => {
        pos[vi * 3] = c[0]; pos[vi * 3 + 1] = c[1]; pos[vi * 3 + 2] = c[2];
      });
      meshes[fi].geometry.attributes.position.needsUpdate = true;

      meshes[fi].material.uniforms.u_fillColor.value.set(pa[0] / 255, pa[1] / 255, pa[2] / 255);
      meshes[fi].material.uniforms.u_fillOpacity.value = 0.06;

      const epos = edges[fi].geometry.attributes.position.array;
      [[c0, c1], [c1, c2], [c2, c3], [c3, c0]].forEach(([a, b], si) => {
        epos[si * 6]     = a[0]; epos[si * 6 + 1] = a[1]; epos[si * 6 + 2] = a[2];
        epos[si * 6 + 3] = b[0]; epos[si * 6 + 4] = b[1]; epos[si * 6 + 5] = b[2];
      });
      edges[fi].geometry.attributes.position.needsUpdate = true;
      edges[fi].material.color.setRGB(pa[0] / 255, pa[1] / 255, pa[2] / 255);
      edges[fi].material.opacity = 0.5;
    });

    // ── Dashed depth edges ──
    const depthPairs = [[2, 3], [3, 1], [3, 7]];
    const dpos = dashed.geometry.attributes.position.array;
    depthPairs.forEach(([ai, bi], i) => {
      dpos[i * 6]     = C[ai][0]; dpos[i * 6 + 1] = C[ai][1]; dpos[i * 6 + 2] = C[ai][2];
      dpos[i * 6 + 3] = C[bi][0]; dpos[i * 6 + 4] = C[bi][1]; dpos[i * 6 + 5] = C[bi][2];
    });
    dashed.geometry.attributes.position.needsUpdate = true;
    dashed.computeLineDistances();

    const dashWorld = 3 / sf;
    const isLight = theme === 'light';
    dashed.material.color.setRGB(isLight ? 0 : 1, isLight ? 0 : 1, isLight ? 0 : 1);
    dashed.material.opacity = isLight ? 0.12 : 0.13;
    dashed.material.dashSize = dashWorld;
    dashed.material.gapSize = dashWorld;

    // ── ISO mesh uniforms — updated every frame ──
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const iu = isoMesh.material.uniforms;
    iu.u_W3.value = W3;
    iu.u_H3.value = H3;
    iu.u_D3.value = D3;
    iu.u_cosAz.value = cosAz;
    iu.u_sinAz.value = sinAz;
    iu.u_cosEl.value = cosEl;
    iu.u_sinEl.value = sinEl;
    iu.u_minU.value = minU;
    iu.u_minV.value = minV;
    iu.u_sf.value = sf;
    iu.u_offX.value = offX;
    iu.u_offY.value = offY;
    iu.u_containerH.value = containerH;
    iu.u_dpr.value = dpr;
    const interacting = isDragging || dimInteracting;
    iu.u_step.value = interacting ? 3.5 : (animPlaying ? 2.0 : 1.0);
    iu.u_isoVisible.value = showIso ? 1.0 : 0.0;

    // ── Draw ──
    renderer.render(sceneRef.current, camera);

    // ── Dimension labels (positioned via CSS transforms) ──
    const ra = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;

    function labelPos(iA, iB, offset = 16) {
      const p1 = P[iA], p2 = P[iB];
      const mu = (p1.u + p2.u) / 2, mv = (p1.v + p2.v) / 2;
      const du = p2.u - p1.u, dv = p2.v - p1.v;
      const len = Math.sqrt(du * du + dv * dv) || 1;
      const n1u = -dv / len, n1v = du / len;
      const dot = n1u * mu + n1v * mv;
      const [nu, nv] = dot >= 0 ? [n1u, n1v] : [-n1u, -n1v];
      return toC({ u: mu + nu * offset, v: mv + nv * offset });
    }

    const fontSize = Math.round(6 * sf);
    const dimLabel = (val) => imperial ? formatFtInSymbol(val) : `${val.toFixed(1)}m`;

    function updateLabel(ref, pos, color, text) {
      const el = ref.current;
      if (!el) return;
      el.style.transform = `translate(-50%,-50%) translate(${pos.x}px,${pos.y}px)`;
      el.style.color = color;
      el.style.fontSize = `${fontSize}px`;
      el.textContent = text;
    }

    const [paL2, paH2, paW2] = COLORMAPS[activeColormap]._pa;
    updateLabel(labelLRef, labelPos(0, 1),     ra(paL2, 0.9), `L = ${dimLabel(Lx)}`);
    updateLabel(labelHRef, labelPos(5, 7, 24), ra(paH2, 0.9), `H = ${dimLabel(Lz)}`);
    updateLabel(labelWRef, labelPos(1, 5),     ra(paW2, 0.9), `W = ${dimLabel(Ly)}`);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [az, el, Lx, Ly, Lz, containerW, containerH, imperial, theme, zoom, activeColormap, mode, showIso, isDragging, dimInteracting, animPlaying]);

  // ── Animation rAF — updates u_phase on all shaders, renders at 60 fps ──
  useEffect(() => {
    if (!animPlaying || !mode) return;
    let rafId;
    let lastTime = null;
    const OMEGA = 2 * Math.PI / 3; // 1 full cycle per 3 seconds
    function step(now) {
      const dt = lastTime !== null ? (now - lastTime) / 1000 : 0;
      lastTime = now;
      animPhaseRef.current = (animPhaseRef.current + OMEGA * dt) % (2 * Math.PI);
      const phase = animPhaseRef.current;
      if (onPhaseUpdate) onPhaseUpdate(phase);
      if (faceMeshesRef.current) {
        faceMeshesRef.current.forEach(m => { m.material.uniforms.u_phase.value = phase; });
      }
      if (isoMeshRef.current) {
        isoMeshRef.current.material.uniforms.u_phase.value = phase;
      }
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      rafId = requestAnimationFrame(step);
    }
    rafId = requestAnimationFrame(step);
    animRafRef.current = rafId;
    return () => {
      cancelAnimationFrame(rafId);
      animRafRef.current = null;
      // Phase intentionally NOT reset — persists in animPhaseRef and shader
      // uniforms so pause/resume and mode switches keep the current phase.
    };
  }, [animPlaying, mode, onPhaseUpdate]);

  // ── Phase reset (triggered by ⏮ button) ──────────────────────
  useEffect(() => {
    if (!phaseResetTrigger) return; // skip initial mount (count === 0)
    animPhaseRef.current = 0;
    if (onPhaseUpdate) onPhaseUpdate(0);
    // Update shader uniforms immediately
    if (faceMeshesRef.current) {
      faceMeshesRef.current.forEach(m => { m.material.uniforms.u_phase.value = 0; });
    }
    if (isoMeshRef.current) {
      isoMeshRef.current.material.uniforms.u_phase.value = 0;
    }
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  }, [phaseResetTrigger, onPhaseUpdate]);

  // ── Wheel zoom ─────────────────────────────────────────────────
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;
    function onWheel(e) {
      e.preventDefault();
      targetPoseRef.current = {
        ...targetPoseRef.current,
        zoom: Math.max(0.2, Math.min(4.0, targetPoseRef.current.zoom * (1 - e.deltaY * 0.001))),
      };
      if (!poseRafRef.current) {
        poseRafRef.current = requestAnimationFrame(() => {
          poseRafRef.current = null;
          const p = targetPoseRef.current;
          setAz(p.az); setEl(p.el); setZoom(p.zoom);
        });
      }
    }
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
    };
  }, []);

  // ── Mouse / touch start ────────────────────────────────────────
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;
    function onMouseDown(e) {
      dragRef.current = {
        startX: e.clientX, startY: e.clientY,
        startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el,
      };
      setIsDragging(true);
      e.preventDefault();
    }
    function onTouchStart(e) {
      if (e.touches.length === 2) {
        const t0 = e.touches[0], t1 = e.touches[1];
        const cx = (t0.clientX + t1.clientX) / 2;
        const cy = (t0.clientY + t1.clientY) / 2;
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        dragRef.current = { startX: cx, startY: cy, startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el };
        pinchRef.current = { startDist: dist, startZoom: targetPoseRef.current.zoom };
      } else {
        const t = e.touches[0];
        dragRef.current = { startX: t.clientX, startY: t.clientY, startAz: targetPoseRef.current.az, startEl: targetPoseRef.current.el };
        pinchRef.current = null;
      }
      setIsDragging(true);
      e.preventDefault();
    }
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('touchstart', onTouchStart);
    };
  }, []);

  // ── Drag move / release ────────────────────────────────────────
  useEffect(() => {
    if (!isDragging) return;
    function flush() {
      if (poseRafRef.current) return;
      poseRafRef.current = requestAnimationFrame(() => {
        poseRafRef.current = null;
        const p = targetPoseRef.current;
        setAz(p.az); setEl(p.el); setZoom(p.zoom);
      });
    }
    function onMove(ev) {
      if (!dragRef.current) return;
      targetPoseRef.current = {
        ...targetPoseRef.current,
        az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + (ev.clientX - dragRef.current.startX) * 0.005)),
        el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + (ev.clientY - dragRef.current.startY) * 0.005)),
      };
      flush();
    }
    function onTouchMove(ev) {
      if (!dragRef.current) return;
      ev.preventDefault();
      if (ev.touches.length === 2 && pinchRef.current) {
        const t0 = ev.touches[0], t1 = ev.touches[1];
        const cx = (t0.clientX + t1.clientX) / 2;
        const cy = (t0.clientY + t1.clientY) / 2;
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        targetPoseRef.current = {
          ...targetPoseRef.current,
          az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + (cx - dragRef.current.startX) * 0.005)),
          el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + (cy - dragRef.current.startY) * 0.005)),
          zoom: Math.max(0.2, Math.min(4.0, pinchRef.current.startZoom * (dist / pinchRef.current.startDist))),
        };
        flush();
      } else if (ev.touches.length === 1) {
        const t = ev.touches[0];
        targetPoseRef.current = {
          ...targetPoseRef.current,
          az: Math.max(AZ_MIN, Math.min(AZ_MAX, dragRef.current.startAz + (t.clientX - dragRef.current.startX) * 0.005)),
          el: Math.max(EL_MIN, Math.min(EL_MAX, dragRef.current.startEl + (t.clientY - dragRef.current.startY) * 0.005)),
        };
        flush();
      }
    }
    function onUp() {
      dragRef.current = null;
      if (poseRafRef.current) { cancelAnimationFrame(poseRafRef.current); poseRafRef.current = null; }
      const p = targetPoseRef.current;
      setAz(p.az); setEl(p.el); setZoom(p.zoom);
      setIsDragging(false);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging]);

  // ── JSX ────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{
      width: '100%', flex: 1, minHeight: 0, position: 'relative',
      cursor: isDragging ? 'grabbing' : 'grab',
    }}>
      {/* Three.js canvas is inserted as first child by the init effect */}

      {/* Dimension labels — positioned imperatively by the render effect */}
      <div ref={labelLRef} style={labelStyle} />
      <div ref={labelHRef} style={labelStyle} />
      <div ref={labelWRef} style={labelStyle} />

      {/* Colormap picker */}
      <div style={{ position: 'absolute', top: 8, left: 8 }}>
        <ColormapPicker value={activeColormap} onChange={setActiveColormap} />
      </div>

      {/* ISO toggle */}
      {mode && (
        <button
          aria-pressed={showIso}
          onClick={() => setShowIso(v => !v)}
          style={{
            position: 'absolute', top: 8, right: 8,
            background: showIso ? hexRgba(COLORMAPS[activeColormap].accents[0], 0.12) : 'rgba(138,143,152,0.1)',
            border: `1px solid ${showIso ? hexRgba(COLORMAPS[activeColormap].accents[0], 0.4) : 'rgba(138,143,152,0.25)'}`,
            color: showIso ? COLORMAPS[activeColormap].accents[0] : '#5A5F68',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
            fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          ISO {showIso ? 'ON' : 'OFF'}
        </button>
      )}
    </div>
  );
}

const labelStyle = {
  position: 'absolute', top: 0, left: 0,
  pointerEvents: 'none',
  fontFamily: "'DM Mono', monospace",
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
